import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, Dictionary } from '@reduxjs/toolkit';
import normalize from 'json-api-normalizer';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { JSONApiDataObject, ReduxEntity, UserType } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import {
  jobBidAddProPaymentRequestRelation,
  jobBidRemoveProPaymentRequestRelation
} from 'Douleutaras/redux/data/jobBids/jobBidsSlice';

export const API_PRO_PAYMENT_REQUEST_ENDPOINT = 'api/public/v3/pro-payment-request';
export const API_CUSTOMER_PAYMENT_REQUEST_ENDPOINT = 'api/public/v3/customer-pro-payment-request';
export const MODEL_TYPE = 'pro-payment-requests';

const proPaymentRequestAdapter = createEntityAdapter<JSONApiDataObject<ProPaymentRequest | CustomerProPaymentRequest>>();

export enum PaymentRequestStatus {
  ACCEPTED = 'accepted',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export interface ProPaymentRequest {
  status: PaymentRequestStatus;
  currencyCode: string;
  amount: string;
  netAmount: string;
  description: string;
}

export interface CustomerProPaymentRequest {
  id: number;
  allowOnlinePayment: boolean;
  amount: string;
  bidId: string;
  categoryTitle: string;
  currencyCode: string;
  description: string;
  jobId: number;
  netAmount: string;
  proMugshot: string;
  proName: string;
  status: PaymentRequestStatus;
}

export interface PostProRequestProps {
  amount: string;
  description: string;
  bidId: string;
  status?: string;
}

interface ProPaymentRequestStatusChangeProps {
  reqId: string;
  bidId: string;
  userType: UserType;
  newStatus?: PaymentRequestStatus;
}

export const postProRequest = createAsyncThunk<JSONApiDataObject<ProPaymentRequest>, PostProRequestProps>(
  'proPaymentRequest/post',
  async (props, api) => {
    const response = await makeRequest(API_PRO_PAYMENT_REQUEST_ENDPOINT, {
      method: 'POST',
      body: {
        data: {
          type: MODEL_TYPE,
          attributes: {
            ...props,
            status: PaymentRequestStatus.IN_PROGRESS
          }
        }
      }
    })(api);

    const id = response.data.data.id;
    const normalized = normalize(response.data, { endpoint: `${API_PRO_PAYMENT_REQUEST_ENDPOINT}/${props.bidId}` });

    // Request was created
    if (response.status === 200 || response.status === 201) {
      await api.dispatch(jobBidAddProPaymentRequestRelation({
        bidId: props.bidId,
        data: {
          id,
          type: 'proPaymentRequests'
        }
      }));
    }

    return normalized.proPaymentRequests[id];
  }
);

export const cancelProRequest = createAsyncThunk<string, ProPaymentRequestStatusChangeProps>(
  'proPaymentRequest/status/cancelled',
  async (props, api) => {
    const response = await api.dispatch(proPaymentRequestStatusChange({
      ...props,
      newStatus: PaymentRequestStatus.CANCELLED
    }));

    // Request was canceled
    if (response.meta.requestStatus === 'fulfilled') {
      await api.dispatch(jobBidRemoveProPaymentRequestRelation({ bidId: props.bidId }));

      return response.payload as string;
    }

    return api.rejectWithValue(undefined);
  }
);

export const acceptProRequest = createAsyncThunk<string, ProPaymentRequestStatusChangeProps>(
  'proPaymentRequest/status/accepted',
  async (props, api) => {
    const response = await api.dispatch(proPaymentRequestStatusChange({
      ...props,
      newStatus: PaymentRequestStatus.ACCEPTED
    }));

    // Request was accepted
    if (response.meta.requestStatus === 'fulfilled') {
      await api.dispatch(jobBidRemoveProPaymentRequestRelation({ bidId: props.bidId }));
    }

    return response.payload as string;
  }
);

export const proPaymentRequestStatusChange = createAsyncThunk<string, ProPaymentRequestStatusChangeProps>(
  'proPaymentRequest/status/changed',
  async (props, api) => {
    const response = await makeRequest(
      `${props.userType === UserType.SELLER
        ? API_PRO_PAYMENT_REQUEST_ENDPOINT
        : API_CUSTOMER_PAYMENT_REQUEST_ENDPOINT}/${props.reqId}`,
      {
        method: 'PATCH',
        body: {
          data: {
            id: props.reqId,
            type: MODEL_TYPE,
            attributes: {
              status: props.newStatus
            }
          }
        }
      })(api);

    return response.data.data.id;
  }
);

export const markAsSeenCanceledProRequest = createAsyncThunk<string, Partial<ProPaymentRequestStatusChangeProps>>(
  'proPaymentRequest/markAsSeenCanceledRequest',
  async (props, api) => {
    const response = await makeRequest(
      `${props.userType === UserType.SELLER
        ? API_PRO_PAYMENT_REQUEST_ENDPOINT
        : API_CUSTOMER_PAYMENT_REQUEST_ENDPOINT}/${props.reqId}`,
      {
        method: 'PATCH',
        body: {
          data: {
            id: props.reqId,
            type: MODEL_TYPE,
            attributes: {
              isCancellationSeen: true
            }
          }
        }
      })(api);

    return response.data.data.id;
  }
);

export const fetchPaymentRequestsAsCustomer = createAsyncThunk<Dictionary<JSONApiDataObject<CustomerProPaymentRequest>>, void>(
  'proPaymentRequests/AsCustomer/getAll',
  async (props, api) => {
    const response = await makeRequest(API_CUSTOMER_PAYMENT_REQUEST_ENDPOINT, {
      method: 'GET',
      additionalGetParams: {
        'filter[status]': PaymentRequestStatus.IN_PROGRESS
      }
    })(api);

    const normalized = normalize(response.data, { endpoint: API_CUSTOMER_PAYMENT_REQUEST_ENDPOINT });

    return normalized.proPaymentRequests;
  }
);

export const proPaymentRequestSlice = createSlice({
  name: 'proPaymentRequest',
  initialState: proPaymentRequestAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<ProPaymentRequest | CustomerProPaymentRequest>),
  reducers: {
    proRequestsAdded: (state, action) => {
      proPaymentRequestAdapter.setMany(state, action.payload);
    },
    proRequestRemoved: (state, action) => {
      proPaymentRequestAdapter.removeOne(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(postProRequest.pending, state => {
      state.loading = true;
    });
    builder.addCase(postProRequest.fulfilled, (state, action) => {
      state.loading = false;
      proPaymentRequestAdapter.setOne(state, action.payload);
    });
    builder.addCase(cancelProRequest.fulfilled, (state, action) => {
      proPaymentRequestAdapter.removeOne(state, action.payload);
    });
    builder.addCase(acceptProRequest.fulfilled, (state, action) => {
      proPaymentRequestAdapter.removeOne(state, action.payload);
    });
    builder.addCase(fetchPaymentRequestsAsCustomer.fulfilled, (state, action) => {
      proPaymentRequestAdapter.upsertMany(state, action.payload || {} as never);
    });
    builder.addCase(markAsSeenCanceledProRequest.fulfilled, (state, action) => {
      proPaymentRequestAdapter.removeOne(state, action.payload);
    });
  }
});

export const { proRequestsAdded, proRequestRemoved } = proPaymentRequestSlice.actions;
export const proPaymentRequestSelectors = proPaymentRequestAdapter.getSelectors((state: RootState) => state.payments.proPaymentRequest);

export const selectAllRequestsByBidIds = createSelector([
  state => state.jobBids,
  state => state.payments.proPaymentRequest,
  (state, { bidIds }) => ({ bidIds } as { bidIds: Array<string> })
], (bids, requests, { bidIds }) => {
  const results = [];

  bidIds.forEach(bidId => {
    const bid = bids.entities[bidId];

    if (!bid || !bid.relationships.proPaymentRequest?.data || !requests.entities[bid.relationships.proPaymentRequest?.data.id]) {
      return;
    }

    results.push(requests.entities[bid.relationships.proPaymentRequest.data.id]);
  });

  return results;
});

export const selectAllRequestsByIds = createSelector([
  state => state.payments.proPaymentRequest,
  (state, { ids }) => ({ ids } as { ids: Array<string> })
], (requests, { ids }) => {
  const results = [];

  ids.forEach(id => {
    const request = requests.entities[id];

    if (!request) {
      return;
    }

    results.push(request);
  });

  return results;
});
